// Partenaires : une notification et un vrai e-mail (D-147), d'après les maquettes validées (a1, m1, r2, c1) :
//  - « Comment ça part » : l'e-mail aux couleurs du club (son adresse, « <Club> via Arceau », les réponses chez toi) et
//    la notification dans son espace s'il a un compte ; sans e-mail sur sa fiche, « Ajoute son e-mail » ;
//  - « … reçoit » : l'aperçu de l'e-mail (le même rendu que celui que push-dispatch envoie,
//    supabase/functions/_shared/partner-mail.ts), « Voir l'e-mail entier » ;
//  - ce que le bureau voit ensuite : la fiche du partenaire (« Invitation en cours ») et celle du match (« Partenaires
//    invités »), la réponse, l'ouverture du lien, « Relancer » proposé après 3 jours, une fois.
// Rien ne part de l'app : la base met l'e-mail en file et push-dispatch l'envoie.
import { partnerMail, type PartnerMailIn } from '../../supabase/functions/_shared/partner-mail';
import { lienFollowup, lienInviteRemind, lienMailReach, type MailReach } from '../lib/lien-api';
import { answerLine, bonTitle, guestChip, sentLine, sinceText, type FollowInvite } from '../domain/lien';
import type { Ctx } from './context';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { partnerMark } from './partners';
import { openShare } from './share';

const ZERO = '0'.repeat(64);
const isBureau = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);

/** Ce qu'il faut pour écrire l'aperçu : le club (couleur, logo), qui signe, la ville. */
export function previewMail(ctx: Ctx, reach: MailReach | null, o: Omit<PartnerMailIn, 'token' | 'club' | 'signer' | 'replyTo'>) {
  const club = ctx.club!;
  return partnerMail({
    ...o, token: ZERO,
    club: { name: club.name, color: club.primary_color, logo: club.logo_url, city: reach?.city ?? null },
    signer: { name: reach?.me.name ?? null, title: reach?.me.title ?? null }, replyTo: !!reach?.me.reply,
  }, location.origin, 'preview');
}

/** « Comment ça part » : l'e-mail du club et la notification (maquette a1). */
export function waysCards(o: { email: string | null; why: 'ok' | 'none' | 'app_only' | 'stopped'; app: boolean; clubName: string; addEmail?: boolean }): Html {
  const mailSub = o.email ? `À ${o.email} · « ${o.clubName} via Arceau », les réponses arrivent chez toi`
    : o.why === 'app_only' ? 'Il a choisi « Seulement dans l’app » : la notification suffit'
    : o.why === 'stopped' ? 'Il ne reçoit plus les e-mails du club (« Ne plus recevoir ces messages »)'
    : 'Pas d’e-mail sur sa fiche';
  return html`<ul class="pm-ways">
    <li class="${o.email ? 'on' : 'off'}" data-way="mail"><span class="pm-ic" aria-hidden="true"><span class="ti ti-mail"></span></span>
      <span class="pm-t"><strong>Un e-mail aux couleurs du club</strong><span class="muted small" id="pm-mail-sub">${mailSub}</span>
        ${o.addEmail && o.why === 'none' ? html`<label class="field pm-add"><span>Ajoute son e-mail</span><input type="email" id="pm-email" maxlength="200" autocomplete="off" placeholder="contact@entreprise.fr"></label>` : ''}</span></li>
    <li class="${o.app ? 'on' : 'off'}" data-way="app"><span class="pm-ic" aria-hidden="true"><span class="ti ti-bell"></span></span>
      <span class="pm-t"><strong>Une notification dans son espace</strong><span class="muted small">${o.app ? 'Son compte partenaire : une notification, et la réponse dans son espace'
        : o.email || o.why === 'none' ? 'Pas encore de compte partenaire : l’e-mail suffit' : 'Pas encore de compte partenaire'}</span></span></li>
  </ul>`;
}

/** « Comment ça part » des nouvelles, des vœux… : combien par e-mail, dans leur espace, sans e-mail (maquette c1). */
export function waysCount(o: { mail: number; app: string[]; without: string[]; clubName: string }): Html {
  return html`<ul class="pm-ways">
    <li class="${o.mail ? 'on' : 'off'}"><span class="pm-ic" aria-hidden="true"><span class="ti ti-mail"></span></span>
      <span class="pm-t"><strong>Un e-mail aux couleurs du club · ${o.mail}</strong><span class="muted small">« ${o.clubName} via Arceau », les réponses arrivent chez toi</span></span></li>
    <li class="${o.app.length ? 'on' : 'off'}"><span class="pm-ic" aria-hidden="true"><span class="ti ti-bell"></span></span>
      <span class="pm-t"><strong>Dans leur espace partenaire · ${o.app.length}</strong><span class="muted small">${o.app.length ? `${o.app.join(', ')} : une notification` : 'Aucun compte partenaire'}</span></span></li>
    ${o.without.length ? html`<li class="off"><span class="pm-ic" aria-hidden="true">?</span>
      <span class="pm-t"><strong>Sans e-mail sur leur fiche · ${o.without.length}</strong><span class="muted small">${o.without.join(', ')} · <a href="#/partenaires" data-close>ajouter</a></span></span></li>` : ''}
  </ul>`;
}

/** L'aperçu, réduit, avec « Voir l'e-mail entier » (maquette a1). */
export const miniMail = (inner: string, more = true): Html => html`
  <div class="pm-mini"><div class="pm-zoom">${raw(inner)}</div>
    ${more ? html`<button type="button" class="btn btn-ghost btn-sm pm-mini-go" data-full>Voir l’e-mail entier</button>` : ''}</div>`;

/** L'e-mail entier, tel qu'il arrive (maquette m1) : l'expéditeur, le destinataire, les réponses, l'objet. */
export function openFullMail(m: { subject: string; fromName: string; inner: string }, to: string | null, replyName: string | null): void {
  openSheet('L’e-mail du club', html`
    <div class="pm-page">
      <div class="pm-meta"><b>${m.fromName}</b> &lt;noreply@arceau.app&gt;<br>${to ? `À : ${to}` : 'À : son adresse'}${replyName ? ` · Réponses : ${replyName}` : ''}<br>
        Objet : <b>${m.subject}</b></div>
      ${raw(m.inner)}
    </div>
    <p class="hint">Les boutons ouvrent la page de réponse : un clic suffit, sans compte. Rien ne part avant « Envoyer ».</p>`);
}

// =============================================================================================
// Ce que le bureau voit ensuite (maquettes r2)
// =============================================================================================
const giftShort = (i: FollowInvite) => [bonTitle(i.gifts), i.gifts.some(g => g.kind === 'place') ? 'place réservée' : ''].filter(Boolean).join(', ');
const remindHint = 'Sans réponse au bout de 3 jours, l’app propose une relance : un e-mail court et une notification, une seule fois.';

/** Relancer : un e-mail court et une notification, une fois. */
async function remind(btn: HTMLButtonElement, i: FollowInvite, again: () => void) {
  btn.disabled = true;
  try {
    const r = await lienInviteRemind(i.id);
    toast(`Relance envoyée à ${i.partner}${r.mailed && r.notified ? ' : un e-mail et une notification' : r.mailed ? ' par e-mail' : ' dans son espace'}`);
    again();
  } catch (e) {
    btn.disabled = false;
    const m = String((e as Error).message);
    toast(m.includes('une seule') ? 'Déjà relancé : une relance, une seule fois.' : m.includes('3 jours') ? 'Relance possible 3 jours après l’envoi.' : 'Relance impossible pour le moment.', 'ko');
  }
}
/** Le lien de la page de réponse, par WhatsApp ou SMS (sans e-mail ni compte). */
export function shareAnswerLink(partner: string, text: string): void {
  openShare(`Envoyer le lien à ${partner}`, 'Le message et le lien de la page de réponse : un clic suffit, sans compte.', text, {
    footer: html`<p class="hint">Rien ne part tout seul : c’est toi qui envoies, depuis ton téléphone. La réponse arrive sur sa fiche et sur celle du match.</p>`,
  });
}
const shareText = (i: FollowInvite) =>
  `Bonjour, le club vous invite au match ${i.title}, ${i.day.split('-').reverse().slice(0, 2).join('/')} à ${i.time}. Pouvez-vous nous dire si vous venez ? Un clic suffit, sans compte : ${location.origin}/l/${i.token}`;

/** La fiche du partenaire : l'invitation en cours, comment elle est partie, la réponse (maquette r2, g1). */
export function followCard(i: FollowInvite, dayLabel: string): Html {
  const chip = guestChip(i);
  return html`
    <div class="ln-sug pm-follow"><p class="eyebrow">Invitation en cours</p>
      <p><strong>Au match ${i.title}</strong><br><span class="muted small">${[dayLabel, i.host_name ? `accueil : ${i.host_name}` : '', giftShort(i)].filter(Boolean).join(' · ')}</span></p>
      <p class="pm-ans"><span class="chip ${chip.cls}">${chip.text}</span> <span class="small">${answerLine(i)}</span></p>
      ${i.answer_note ? html`<p class="small pm-note">« ${i.answer_note} »</p>` : ''}
      <p class="muted small">${sentLine(i)}</p>
      <div class="btn-row">
        ${i.can_remind ? html`<button type="button" class="btn btn-primary btn-sm" data-remind="${i.id}">Relancer</button>` : ''}
        ${i.via === 'lien' && i.status === 'envoyee' ? html`<button type="button" class="btn btn-ghost btn-sm" data-share="${i.id}">Renvoyer le lien</button>` : ''}
        ${(i.via === 'lien' || i.via === 'messagerie') && i.status === 'envoyee' ? html`<button type="button" class="btn btn-ghost btn-sm" data-mark="1">Vient</button><button type="button" class="btn btn-ghost btn-sm" data-mark="0">Ne vient pas</button>` : ''}
        <button type="button" class="btn btn-ghost btn-sm" id="ln-cancel">Annuler l’invitation</button></div>
      ${i.status === 'envoyee' && !i.can_remind && !i.reminded_at ? html`<p class="hint">${remindHint}</p>` : ''}
    </div>`;
}
export function wireFollow(el: HTMLElement, list: FollowInvite[], again: () => void): void {
  for (const b of $$<HTMLButtonElement>(el, '[data-remind]')) {
    const i = list.find(x => x.id === b.dataset.remind);
    if (i) b.addEventListener('click', () => void remind(b, i, again));
  }
  for (const b of $$<HTMLButtonElement>(el, '[data-share]')) {
    const i = list.find(x => x.id === b.dataset.share);
    if (i) b.addEventListener('click', () => shareAnswerLink(i.partner, shareText(i)));
  }
}

/** La fiche du match, onglet « Avant » : les partenaires invités, qui vient, qui n'a pas répondu (maquette r2). Le bureau seul. */
export async function renderMatchGuests(el: HTMLElement, ctx: Ctx, eventId: string): Promise<void> {
  if (!ctx.club || !isBureau(ctx)) return;
  const list = await lienFollowup(ctx.club.id, eventId).catch(() => [] as FollowInvite[]);
  const again = () => void renderMatchGuests(el, ctx, eventId);
  if (!list.length) { mount(el, html``); return; }
  mount(el, html`
    <section class="block" data-guide="ln-guests"><div class="block-head"><h3>Partenaires invités</h3><span class="chip chip-mute">${list.length}</span></div>
      <ul class="pm-guests">${list.map(i => {
        const chip = guestChip(i);
        const sub = i.status === 'envoyee'
          ? `Invité ${sinceText(i.sent_at)}, ${i.via === 'email' && i.notified ? 'dans son espace et par e-mail' : i.via === 'email' ? 'par e-mail' : i.via === 'app' ? 'dans son espace' : 'par le lien'} · pas encore de réponse`
          : [answerLine(i).replace(/^réponse/, 'Réponse'), i.host_name ? `accueil : ${i.host_name}` : '', bonTitle(i.gifts)].filter(Boolean).join(' · ');
        return html`<li>${partnerMark({ name: i.partner, logo_url: i.logo }, 'pt-logo')}
          <span class="card-main"><a class="pm-who" href="#/partenaires/${i.partner_id}"><strong>${i.partner}</strong></a><span class="muted small">${sub}</span>
            ${i.answer_note ? html`<span class="small pm-note">« ${i.answer_note} »</span>` : ''}
            ${i.can_remind ? html`<span class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-remind="${i.id}">Relancer</button></span>` : ''}</span>
          <span class="chip ${chip.cls}">${chip.text}</span></li>`;
      })}</ul>
      ${list.some(i => i.status === 'envoyee') ? html`<p class="hint">${remindHint}</p>` : ''}
    </section>`);
  wireFollow(el, list, again);
}

/** Les adresses des partenaires (Comment ça part) ; null si la base ne répond pas. */
export const loadReach = (ctx: Ctx) => lienMailReach(ctx.club!.id).catch(() => null);
